import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { normalizePeriodName } from './scheduleTargetUtils';

const TABLE_STYLES = {
  headStyles: {
    fillColor: [222, 243, 245],
    textColor: [20, 20, 20],
    fontStyle: 'bold',
    fontSize: 7,
    halign: 'center',
    valign: 'middle'
  },
  bodyStyles: {
    fontSize: 7,
    halign: 'center',
    valign: 'middle',
    textColor: [20, 20, 20]
  },
  alternateRowStyles: { fillColor: [242, 242, 244] },
  styles: {
    cellPadding: 1.8,
    overflow: 'linebreak',
    lineWidth: 0.1,
    lineColor: [200, 210, 220]
  },
  margin: { left: 8, right: 8 }
};

function sortMeasurementsByTime(measurements = []) {
  return [...measurements].sort((a, b) => {
    const timeA =
      a.measurement_time ||
      a.measure_new_date_time?.split('T')[1] ||
      a.measure_date_time?.split(' ')[1] ||
      a.created_at?.split('T')[1] ||
      '00:00:00';
    const timeB =
      b.measurement_time ||
      b.measure_new_date_time?.split('T')[1] ||
      b.measure_date_time?.split(' ')[1] ||
      b.created_at?.split('T')[1] ||
      '00:00:00';
    return timeB.localeCompare(timeA);
  });
}

function joinCell(values) {
  return values
    .map((v) => (v == null || v === '' ? null : String(v)))
    .filter((v) => v != null)
    .join('\n');
}

function roundValue(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return String(Math.round(n));
}

function fixed1(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toFixed(1);
}

function formatAvg(values) {
  if (!values.length) return '';
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  return Number.isInteger(avg) ? String(avg) : avg.toFixed(2);
}

function getMeasureTimeRaw(measurement) {
  return (
    measurement.measurement_time ||
    measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
    measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
    ''
  );
}

function savePdf(doc, filename) {
  doc.save(filename);
}

function addReportHeader(doc, startY, { title, patient }) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, 10, startY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const lines = [
    `Name: ${patient.name || ''}`,
    `DOB: ${patient.dob || ''}`,
    `Phone: ${patient.phone || ''}`,
    `Device: ${patient.device || ''}`,
    `Generated: ${new Date().toLocaleString()}`
  ];

  let y = startY + 6;
  lines.forEach((line) => {
    doc.text(line, 10, y);
    y += 4.2;
  });

  return y + 2;
}

function renderTables(doc, startY, tables) {
  let y = startY;

  tables.forEach((table, index) => {
    if (!table?.body?.length && !table?.head?.length) return;

    if (table.title) {
      if (y > 175) {
        doc.addPage();
        y = 12;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(table.title, 10, y);
      y += 4;
    }

    if (table.subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(table.subtitle, 10, y);
      y += 4;
    }

    autoTable(doc, {
      startY: y,
      head: table.head,
      body: table.body,
      ...TABLE_STYLES,
      theme: 'grid',
      showHead: table.head?.length ? 'everyPage' : false,
      didDrawPage: (data) => {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.getWidth() - 18, doc.internal.pageSize.getHeight() - 6);
      }
    });

    y = (doc.lastAutoTable?.finalY || y) + (index < tables.length - 1 ? 8 : 0);
  });

  return y;
}

export function exportMeasurementTablesPdf({ filename, title, patient, tables }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const startY = addReportHeader(doc, 12, { title, patient });
  renderTables(doc, startY, tables);
  savePdf(doc, filename);
}

function buildBpMeasurementTable({
  periodList,
  sortedDates,
  groupedMeasurements,
  getResolvedPeriodName,
  formatDate,
  formatTime
}) {
  const headRow1 = [{ content: 'Measurement\nDate', rowSpan: 2 }];
  const headRow2 = [];

  periodList.forEach((periodName) => {
    headRow1.push({ content: periodName, colSpan: 4 });
    headRow2.push('Systolic', 'Diastolic', 'Pulse', 'Measure Time');
  });

  const body = [];

  sortedDates.forEach((date) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const byPeriod = {};

    dateMeasurements.forEach((m) => {
      const resolved = getResolvedPeriodName(m);
      const matched = periodList.find((p) => normalizePeriodName(p) === normalizePeriodName(resolved));
      if (!matched) return;
      if (!byPeriod[matched]) byPeriod[matched] = [];
      byPeriod[matched].push(m);
    });

    const row = [formatDate(date)];
    periodList.forEach((periodName) => {
      const readings = sortMeasurementsByTime(byPeriod[periodName] || []);
      row.push(
        joinCell(readings.map((m) => roundValue(m.systolic_pressure))),
        joinCell(readings.map((m) => roundValue(m.diastolic_pressure))),
        joinCell(readings.map((m) => roundValue(m.pulse))),
        joinCell(readings.map((m) => (getMeasureTimeRaw(m) ? formatTime(getMeasureTimeRaw(m)) : '')))
      );
    });
    body.push(row);

    const avgRow = ['Average'];
    periodList.forEach((periodName) => {
      const readings = byPeriod[periodName] || [];
      const sys = readings.map((m) => parseFloat(m.systolic_pressure)).filter((v) => !Number.isNaN(v));
      const dia = readings.map((m) => parseFloat(m.diastolic_pressure)).filter((v) => !Number.isNaN(v));
      const pulse = readings.map((m) => parseFloat(m.pulse)).filter((v) => !Number.isNaN(v));
      avgRow.push(formatAvg(sys), formatAvg(dia), formatAvg(pulse), '');
    });
    body.push(avgRow);
  });

  return { title: 'Blood Pressure', head: [headRow1, headRow2], body };
}

function buildBpSummaryTable({ periodList, calculatePeriodStats, effectiveMeasurements, totals, formatPercentCount }) {
  const headRow1 = [{ content: 'Summary Statistics', rowSpan: 2 }];
  const headRow2 = [];

  periodList.forEach((periodName) => {
    headRow1.push({ content: periodName, colSpan: 3 });
    headRow2.push('Systolic', 'Diastolic', 'Pulse');
  });

  const statTypes = ['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'];
  const body = statTypes.map((statType) => {
    const row = [statType];
    periodList.forEach((periodName) => {
      const stats = calculatePeriodStats(effectiveMeasurements, periodName);
      if (statType === 'Average') {
        row.push(
          stats.avg?.sys ? roundValue(stats.avg.sys) : '',
          stats.avg?.dia ? roundValue(stats.avg.dia) : '',
          stats.avg?.pulse ? roundValue(stats.avg.pulse) : ''
        );
      } else if (statType === 'Maximum') {
        row.push(
          stats.max?.sys ? roundValue(stats.max.sys) : '',
          stats.max?.dia ? roundValue(stats.max.dia) : '',
          stats.max?.pulse ? roundValue(stats.max.pulse) : ''
        );
      } else if (statType === 'Minimum') {
        row.push(
          stats.min?.sys ? roundValue(stats.min.sys) : '',
          stats.min?.dia ? roundValue(stats.min.dia) : '',
          stats.min?.pulse ? roundValue(stats.min.pulse) : ''
        );
      } else if (statType === 'Count') {
        row.push({ content: String(stats.count ?? ''), colSpan: 3 });
      } else if (statType === 'Above Target') {
        row.push({ content: String(stats.above ?? ''), colSpan: 3 });
      } else if (statType === 'Below Target') {
        row.push({ content: String(stats.below ?? ''), colSpan: 3 });
      } else if (statType === 'Within Target') {
        row.push({ content: String(stats.within ?? ''), colSpan: 3 });
      }
    });
    return row;
  });

  const spanEach = periodList.length;
  body.push([
    'Total',
    {
      content: `Above target\nSystolic pressure: ${formatPercentCount(totals.above.sys, totals.sysTotal)}\nDiastolic pressure: ${formatPercentCount(totals.above.dia, totals.diaTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Within target\nSystolic pressure: ${formatPercentCount(totals.within.sys, totals.sysTotal)}\nDiastolic pressure: ${formatPercentCount(totals.within.dia, totals.diaTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Below target\nSystolic pressure: ${formatPercentCount(totals.below.sys, totals.sysTotal)}\nDiastolic pressure: ${formatPercentCount(totals.below.dia, totals.diaTotal)}`,
      colSpan: spanEach
    }
  ]);

  return { title: 'Summary Of Measurement', head: [headRow1, headRow2], body };
}

export function exportBloodPressureTablesPdf(config) {
  const periodList =
    config.periodList?.length > 0
      ? config.periodList
      : ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];

  const tables = [
    buildBpMeasurementTable({ ...config, periodList }),
    buildBpSummaryTable({ ...config, periodList })
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Blood Pressure Report',
    patient: config.patient,
    tables
  });
}

function buildBgMeasurementTables({
  dateBuckets,
  periodNames,
  groupedMeasurements,
  determinePeriodFromMeasurement,
  formatDate,
  formatTime
}) {
  if (!dateBuckets.length) {
    return [{ title: 'Blood Glucose', body: [['No measurement data available']] }];
  }

  return dateBuckets.map((bucket) => {
    const headRow1 = [{ content: 'Measurement\nDate', rowSpan: 2 }];
    const headRow2 = [];

    periodNames.forEach((periodName) => {
      headRow1.push({ content: periodName, colSpan: 2 });
      headRow2.push('Blood Glucose', 'Measure Time');
    });

    const body = [];

    bucket.dates.forEach((date) => {
      const dateMeasurements = groupedMeasurements[date] || [];
      const byPeriod = {};

      dateMeasurements.forEach((m) => {
        const period = determinePeriodFromMeasurement(m);
        const matched = periodNames.find((p) => normalizePeriodName(p) === normalizePeriodName(period || ''));
        if (!matched) return;
        if (!byPeriod[matched]) byPeriod[matched] = [];
        byPeriod[matched].push(m);
      });

      const row = [formatDate(date)];
      periodNames.forEach((periodName) => {
        const readings = sortMeasurementsByTime(byPeriod[periodName] || []);
        row.push(
          joinCell(
            readings.map((m) => roundValue(m.blood_glucose_value_1 || m.blood_glucose_value))
          ),
          joinCell(readings.map((m) => (getMeasureTimeRaw(m) ? formatTime(getMeasureTimeRaw(m)) : '')))
        );
      });
      body.push(row);
    });

    const avgRow = ['Average'];
    periodNames.forEach((periodName) => {
      const readings = [];
      bucket.dates.forEach((date) => {
        const dateMeasurements = groupedMeasurements[date] || [];
        dateMeasurements.forEach((m) => {
          const period = determinePeriodFromMeasurement(m);
          if (normalizePeriodName(period) === normalizePeriodName(periodName)) readings.push(m);
        });
      });
      const values = readings
        .map((m) => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
        .filter((v) => !Number.isNaN(v));
      avgRow.push(values.length ? roundValue(formatAvg(values)) : '-', '');
    });
    body.push(avgRow);

    return {
      title: 'Blood Glucose',
      subtitle: bucket.label,
      head: [headRow1, headRow2],
      body
    };
  });
}

function buildBgSummaryTable({
  summaryPeriodColumns,
  calculatePeriodStats,
  effectiveMeasurements,
  rangeTotals,
  totalReadingCount,
  formatPercentCount,
  totalRowLayout
}) {
  const headRow1 = [{ content: 'Statistics', rowSpan: 2 }];
  const headRow2 = [];

  const groups = [];
  summaryPeriodColumns.forEach((col) => {
    const existing = groups.find((g) => g.group === col.group);
    if (existing) existing.items.push(col);
    else groups.push({ group: col.group, items: [col] });
  });

  groups.forEach((group) => {
    if (group.items.length === 1 && group.items[0].isSingleHeader) {
      headRow1.push({ content: group.group, rowSpan: 2 });
    } else {
      headRow1.push({ content: group.group, colSpan: group.items.length });
      group.items.forEach((item) => {
        if (!item.isSingleHeader) headRow2.push(item.sub || item.periodName);
      });
    }
  });

  const statTypes = ['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'];
  const body = statTypes.map((statType) => {
    const row = [statType];
    summaryPeriodColumns.forEach((col) => {
      const stats = calculatePeriodStats(effectiveMeasurements, col.periodName);
      if (statType === 'Average') row.push(stats.avg ? roundValue(stats.avg) : '');
      else if (statType === 'Maximum') row.push(stats.max ? roundValue(stats.max) : '');
      else if (statType === 'Minimum') row.push(stats.min ? roundValue(stats.min) : '');
      else if (statType === 'Count') row.push(String(stats.count ?? ''));
      else if (statType === 'Above Target') row.push(String(stats.above ?? ''));
      else if (statType === 'Below Target') row.push(String(stats.below ?? ''));
      else if (statType === 'Within Target') row.push(String(stats.within ?? ''));
    });
    return row;
  });

  body.push([
    'Total',
    {
      content: `Below Target: ${formatPercentCount(rangeTotals.below, totalReadingCount)}`,
      colSpan: totalRowLayout.beforeMealCols
    },
    {
      content: `Above Target: ${formatPercentCount(rangeTotals.above, totalReadingCount)}`,
      colSpan: totalRowLayout.afterMealCols
    },
    {
      content: `Within Target: ${formatPercentCount(rangeTotals.within, totalReadingCount)}`,
      colSpan: totalRowLayout.remainingCols
    }
  ]);

  return { title: 'Summary Of Measurement', head: headRow2.length ? [headRow1, headRow2] : [headRow1], body };
}

export function exportBloodGlucoseTablesPdf(config) {
  const tables = [
    ...buildBgMeasurementTables(config),
    buildBgSummaryTable(config)
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Blood Glucose Report',
    patient: config.patient,
    tables
  });
}

function buildWeightMeasurementTables({
  dateBuckets,
  periodList,
  groupedMeasurements,
  resolvePeriodForMeasurement,
  formatDate,
  formatTime
}) {
  if (!dateBuckets.length) {
    return [{ title: 'Weight', body: [['No measurement data available']] }];
  }

  return dateBuckets.map((bucket) => {
    const headRow1 = [{ content: 'Date', rowSpan: 2 }];
    const headRow2 = [];

    periodList.forEach((periodName) => {
      headRow1.push({ content: periodName, colSpan: 4 });
      headRow2.push('Weight', 'Body Fat', 'BMI', 'Measure Time');
    });

    const body = [];

    bucket.dates.forEach((date) => {
      const byPeriod = resolvePeriodForMeasurement(date, periodList);
      const row = [formatDate(date)];
      periodList.forEach((periodName) => {
        const readings = sortMeasurementsByTime(byPeriod[periodName] || []);
        row.push(
          joinCell(readings.map((m) => fixed1(m.weight))),
          joinCell(readings.map((m) => fixed1(m.fat || m.body_fat))),
          joinCell(readings.map((m) => fixed1(m.bmi))),
          joinCell(readings.map((m) => (getMeasureTimeRaw(m) ? formatTime(getMeasureTimeRaw(m)) : '')))
        );
      });
      body.push(row);
    });

    const avgRow = ['Average'];
    periodList.forEach((periodName) => {
      const readings = [];
      bucket.dates.forEach((date) => {
        const byPeriod = resolvePeriodForMeasurement(date, periodList);
        readings.push(...(byPeriod[periodName] || []));
      });
      const wVals = readings.map((m) => parseFloat(m.weight)).filter((v) => !Number.isNaN(v));
      const fVals = readings.map((m) => parseFloat(m.fat || m.body_fat)).filter((v) => !Number.isNaN(v));
      const bVals = readings.map((m) => parseFloat(m.bmi)).filter((v) => !Number.isNaN(v));
      avgRow.push(
        wVals.length ? formatAvg(wVals) : '-',
        fVals.length ? formatAvg(fVals) : '-',
        bVals.length ? formatAvg(bVals) : '-',
        ''
      );
    });
    body.push(avgRow);

    return {
      title: 'Weight',
      subtitle: bucket.label,
      head: [headRow1, headRow2],
      body
    };
  });
}

function buildWeightSummaryTable({
  periodList,
  calculatePeriodStats,
  effectiveMeasurements,
  calculateOverallTargetStats,
  formatPercentCount
}) {
  const headRow1 = [{ content: 'Statistics', rowSpan: 2 }];
  const headRow2 = [];

  periodList.forEach((periodName) => {
    headRow1.push({ content: periodName, colSpan: 3 });
    headRow2.push('Weight', 'Body Fat', 'BMI');
  });

  const statTypes = ['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'];
  const body = statTypes.map((statType) => {
    const row = [statType];
    periodList.forEach((periodName) => {
      const stats = calculatePeriodStats(effectiveMeasurements, periodName);
      if (statType === 'Average') {
        row.push(
          stats.avg?.weight ? fixed1(stats.avg.weight) : '',
          stats.avg?.fat ? fixed1(stats.avg.fat) : '',
          stats.avg?.bmi ? fixed1(stats.avg.bmi) : ''
        );
      } else if (statType === 'Maximum') {
        row.push(
          stats.max?.weight ? fixed1(stats.max.weight) : '',
          stats.max?.fat ? fixed1(stats.max.fat) : '',
          stats.max?.bmi ? fixed1(stats.max.bmi) : ''
        );
      } else if (statType === 'Minimum') {
        row.push(
          stats.min?.weight ? fixed1(stats.min.weight) : '',
          stats.min?.fat ? fixed1(stats.min.fat) : '',
          stats.min?.bmi ? fixed1(stats.min.bmi) : ''
        );
      } else if (statType === 'Count') {
        row.push({ content: String(stats.count ?? ''), colSpan: 3 });
      } else if (statType === 'Above Target') {
        row.push({ content: String(stats.above ?? ''), colSpan: 3 });
      } else if (statType === 'Below Target') {
        row.push({ content: String(stats.below ?? ''), colSpan: 3 });
      } else if (statType === 'Within Target') {
        row.push({ content: String(stats.within ?? ''), colSpan: 3 });
      }
    });
    return row;
  });

  const stats = calculateOverallTargetStats();
  const weightTotal = (stats.above.weight || 0) + (stats.below.weight || 0) + (stats.within.weight || 0);
  const bmiTotal = (stats.above.bmi || 0) + (stats.below.bmi || 0) + (stats.within.bmi || 0);
  const fatTotal = (stats.above.fat || 0) + (stats.below.fat || 0) + (stats.within.fat || 0);
  const spanEach = periodList.length;

  body.push([
    'Total',
    {
      content: `Below target\nWeight: ${formatPercentCount(stats.below.weight, weightTotal)}\nBmi: ${formatPercentCount(stats.below.bmi, bmiTotal)}\nFat: ${formatPercentCount(stats.below.fat, fatTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Within target\nWeight: ${formatPercentCount(stats.within.weight, weightTotal)}\nBmi: ${formatPercentCount(stats.within.bmi, bmiTotal)}\nFat: ${formatPercentCount(stats.within.fat, fatTotal)}`,
      colSpan: spanEach
    },
    {
      content: `Above target\nWeight: ${formatPercentCount(stats.above.weight, weightTotal)}\nBmi: ${formatPercentCount(stats.above.bmi, bmiTotal)}\nFat: ${formatPercentCount(stats.above.fat, fatTotal)}`,
      colSpan: spanEach
    }
  ]);

  return { title: 'Summary Of Measurement', head: [headRow1, headRow2], body };
}

export function exportWeightTablesPdf(config) {
  const periodList =
    config.periodList?.length > 0 ? config.periodList : ['Morning', 'Afternoon', 'Evening'];

  const tables = [
    ...buildWeightMeasurementTables({ ...config, periodList }),
    buildWeightSummaryTable({ ...config, periodList })
  ];

  exportMeasurementTablesPdf({
    filename: config.filename,
    title: config.title || 'Weight Report',
    patient: config.patient,
    tables
  });
}
